/*
 * Safety Monitoring & Measurement Form
 */

import React, { useRef, useEffect, useState } from "react";
import { withSnackbar } from "notistack";
import { IconButton, Tooltip } from "@mui/material";
import { Edit as IconEdit } from "@mui/icons-material";

import UiDialog from "components/Custom/UiDialog";
import ExcelGrid from "components/Custom/ExcelGrid";
import AutoComplete from "components/Custom/AutoComplete";
import SkeletonBox from "components/Custom/SkeletonBox";
import ModalBtmBtns from "components/Custom/ModalBtmBtns";
import Loader from "components/Custom/Loader";
import HGraph from "components/Custom/HGraph";
import Btn from "components/Custom/Btn";
import Part1 from "./sections/Part1";
import PartLast from "./sections/PartLast";

import { LABELS, NOTIFY, SUCCESS, WARNINGS } from "common/config";
import { ApiHome } from "apihandlers/home";
import { ApiEhs } from "apihandlers/ehs";
import { COMMON_JS } from "common/scripts";
import TxtBox from "components/Custom/TxtBox";

const API_HANDLER_HOME = new ApiHome();
const API_HANDLER_EHS = new ApiEhs();

const WasteManagement = (props: any) => {
  const {} = props;
  const mainGridCols = [
    // {
    //   key: "id",
    //   name: "ID",
    //   type: "number",
    //   width: "5%",
    // },
    {
      key: "slNo",
      name: "Sl No.",
      type: "number",
      width: "5%",
      editable: false,
      sortable: true,
      align: "center",
      searchable: true,
    },
    {
      key: "projectName",
      name: "Project Name",
      type: "text",
      width: "30%",
      editable: false,
      sortable: true,
      searchable: true,
    },
    {
      key: "orgName",
      name: "Organization",
      type: "text",
      width: "15%",
      editable: false,
      sortable: true,
      searchable: true,
    },
    {
      key: "createdOn",
      name: "Created On",
      type: "text",
      width: "15%",
      editable: false,
      sortable: true,
      searchable: true,
    },
    {
      key: "createdBy",
      name: "Created By",
      type: "text",
      width: "20%",
      editable: false,
      sortable: true,
      searchable: true,
    },
    {
      key: "options",
      name: "Options",
      type: "html",
      width: "15%",
      editable: false,
      sortable: false,
      renderCell: ({ row }: any) => (
        <div className="options">
          <Tooltip title={LABELS.EDIT} placement="bottom">
            <IconButton className="iconBtn" onClick={() => onEditRow(row)}>
              <IconEdit />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];
  const [loading, setLoading] = useState<boolean>(true);
  const [projects, setProjects] = useState<any[]>([]);
  const [cols, setCols] = useState<any[]>(mainGridCols);
  const [rows, setRows] = useState<any[]>(null);
  const [columnChart, setColumnChart] = useState<any>({});
  const [pieChart, setPieChart] = useState<any>({});
  const [manageDialog, setManageDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>({});
  const [editRows, setEditRows] = useState<any[]>(null);
  const [pageNo, setPageNo] = useState(1);
  const [totalPages, setTotalPages] = useState(6);
  const [formData, setFormData] = useState<any>({});
  const [uploads, setUploads] = useState<any[]>([]);
  const [orgList, setOrgList] = useState<any[]>([]);

  const projectsRef: any = useRef();
  const part1Ref: any = useRef();
  const orgRef: any = useRef(null);
  const part2Ref: any = useRef();
  const part3Ref: any = useRef();
  const part4Ref: any = useRef();
  const part5Ref: any = useRef();
  const part6Ref: any = useRef();

  useEffect(() => {
    return () => {
      cleanUp();
    };
  }, []);

  useEffect(() => {
    if (loading) {
      getProjects();
    }
  }, [loading]);

  const getProjects = () => {
    const args = { dropdown: true };
    API_HANDLER_HOME.getProjects(args).then((res: any) => {
      const response: any = res.response;
      const { status } = res;
      if (status === 200) {
        setProjects(response.records);
        getOrgs();
      }
    });
  };

  const getOrgs = () => {
    const args = {};
    API_HANDLER_HOME.getOrganizations(args).then((res: any) => {
      const response: any = res.response;
      const { status } = res;
      if (status === 200) {
        setOrgList(
          response.records.map((o: any) => {
            return {
              id: o.id,
              label: o.orgName,
            };
          })
        );
        getRows();
      }
    });
  };

  const cleanUp = () => {
    setRows(null);
    setLoading(true);
    setManageDialog(false);
    setSelectedRow({});
    console.log("Cleaned up");
  };

  const getRows = () => {
    setRows(null);
    const args = {};
    API_HANDLER_EHS.getWasteManagements(args).then((res: any) => {
      const { status, response } = res;
      if (status === 200) {
        setRows(response.records);
        setColumnChart(response.columnChart);
        setPieChart(response.pieChart);
        setLoading(false);
      }
    });
  };

  const onAdd = () => {
    const projectId = projectsRef.current.selectedValue.id;
    if (!projectId) {
      COMMON_JS.showNotify(props, NOTIFY.WARNING, WARNINGS.PROJECT_EMPTY);
      return;
    }
    setManageDialog(!manageDialog);
    setEditRows([]);
    setPageNo(1);
    setSelectedRow({});
    setUploads([]);
  };

  const onEditRow = (row: any) => {
    setPageNo(1);
    setSelectedRow(row);
    setManageDialog(true);
    setEditRows(null);
    getRowInfo(row.id, row);
  };

  const getRowInfo = (wasteManageId, row) => {
    const args = {
      wasteManageId,
    };
    API_HANDLER_EHS.getWasteManagementInfo(args).then((res: any) => {
      const { status, response } = res;
      if (status === 200) {
        response["records"] = JSON.parse(row["wasteManageDetails"]);
        setEditRows(response);
      }
    });
  };

  const getProjectInfo = () => {
    const projectId = projectsRef.current.selectedValue.id;
    const args = {
      projectId,
    };
    API_HANDLER_EHS.getWasteManagements(args).then((res: any) => {
      const { status, response } = res;
      if (status === 200) {
        setColumnChart(response.columnChart);
        setPieChart(response.pieChart);
      }
    });
  };

  const manageWasteManagement = () => {
    let args: any = updateForm();

    args = {
      ...args,
      wasteManageId: selectedRow.id || -1,
      orgId: orgRef.current.selectedValue.id,
      projectId: selectedRow.id
        ? selectedRow.projectId
        : projectsRef.current.selectedValue.id,
      files: uploads,
      subject: (document.getElementById("subject") as HTMLInputElement).value,
    };

    console.log("manageWasteManagement: ", args);
    // Validations
    if (!args.subject) {
      COMMON_JS.showNotify(props, NOTIFY.WARNING, "Please enter subject..!");
      return;
    }
    if (!args.projectId) {
      COMMON_JS.showNotify(props, NOTIFY.WARNING, WARNINGS.PROJECT_EMPTY);
      return;
    }
    if (!args.orgId) {
      COMMON_JS.showNotify(
        props,
        NOTIFY.WARNING,
        "Please select Organization..!"
      );
      return;
    }
    if (
      (!args.wasteManageDetails[0].checkYes &&
        !args.wasteManageDetails[0].checkNo &&
        !args.wasteManageDetails[0].checkNa) ||
      (!args.wasteManageDetails[1].checkYes &&
        !args.wasteManageDetails[1].checkNo &&
        !args.wasteManageDetails[1].checkNa) ||
      (!args.wasteManageDetails[2].checkYes &&
        !args.wasteManageDetails[2].checkNo &&
        !args.wasteManageDetails[2].checkNa) ||
      (!args.wasteManageDetails[3].checkYes &&
        !args.wasteManageDetails[3].checkNo &&
        !args.wasteManageDetails[3].checkNa) ||
      (!args.wasteManageDetails[4].checkYes &&
        !args.wasteManageDetails[4].checkNo &&
        !args.wasteManageDetails[4].checkNa) ||
      (!args.wasteManageDetails[5].checkYes &&
        !args.wasteManageDetails[5].checkNo &&
        !args.wasteManageDetails[5].checkNa) ||
      (!args.wasteManageDetails[6].checkYes &&
        !args.wasteManageDetails[6].checkNo &&
        !args.wasteManageDetails[6].checkNa) ||
      (!args.wasteManageDetails[7].checkYes &&
        !args.wasteManageDetails[7].checkNo &&
        !args.wasteManageDetails[7].checkNa) ||
      (!args.wasteManageDetails[8].checkYes &&
        !args.wasteManageDetails[8].checkNo &&
        !args.wasteManageDetails[8].checkNa) ||
      (!args.wasteManageDetails[9].checkYes &&
        !args.wasteManageDetails[9].checkNo &&
        !args.wasteManageDetails[9].checkNa) ||
      (!args.wasteManageDetails[10].checkYes &&
        !args.wasteManageDetails[10].checkNo &&
        !args.wasteManageDetails[10].checkNa) ||
      (!args.wasteManageDetails[11].checkYes &&
        !args.wasteManageDetails[11].checkNo &&
        !args.wasteManageDetails[11].checkNa) ||
      (!args.wasteManageDetails[12].checkYes &&
        !args.wasteManageDetails[12].checkNo &&
        !args.wasteManageDetails[12].checkNa) ||
      (!args.wasteManageDetails[13].checkYes &&
        !args.wasteManageDetails[13].checkNo &&
        !args.wasteManageDetails[13].checkNa) ||
      (!args.wasteManageDetails[14].checkYes &&
        !args.wasteManageDetails[14].checkNo &&
        !args.wasteManageDetails[14].checkNa)
    ) {
      COMMON_JS.showNotify(
        props,
        NOTIFY.WARNING,
        "Please answer all the questions..!"
      );
      return;
    }
    if (!args.recommendations) {
      COMMON_JS.showNotify(
        props,
        NOTIFY.WARNING,
        "Please enter recommendations..!"
      );
      return;
    }
    if (!args.files) {
      COMMON_JS.showNotify(props, NOTIFY.WARNING, "Please upload a file..!");
      return;
    }
    setEditRows(null);
    API_HANDLER_EHS.manageWasteManagement(args).then((res) => {
      setEditRows([]);
      if (res.status === 200) {
        COMMON_JS.showNotify(
          props,
          NOTIFY.SUCCESS,
          SUCCESS.SAFETY_MONITOR_SAVED
        );
        setSelectedRow({});
        setManageDialog(false);
        getRows();
      } else {
        COMMON_JS.showNotify(props, NOTIFY.ERROR, res.error);
      }
    });
  };

  const updateForm = () => {
    let args: any = {
      wasteManageDetails: [],
      files: [],
      recommendations: "",
    };
    if (part1Ref.saveRecord) {
      let tmp = part1Ref.saveRecord();
      if (tmp.items) {
        let tmpRows = Object.values(tmp.items);
        for (let i in tmpRows) {
          args.wasteManageDetails.push(tmpRows[i]);
        }
      }
    }
    if (part6Ref.saveRecord) {
      let tmp = part6Ref.saveRecord();
      if (tmp.files) {
        args.files = tmp.files;
      }
      if (tmp.recommendations) {
        args.recommendations = tmp.recommendations;
      }
    }
    setFormData(args);
    return args;
  };

  const onReload = () => {
    getRows();
  };

  const onCloseForm = () => {
    setManageDialog(false);
  };

  console.log("formData:", formData);

  return (
    <React.Fragment>
      <div className="pageInfo">
        <ul className="breadcrumbs">
          <li>{LABELS.HOME}</li>
          <li className="dim">{" / "}</li>
          <li onClick={() => props.setDashMenu(LABELS.HOME)}>{LABELS.EHS}</li>
          <li className="dim">{" / "}</li>
          <li className="active">{LABELS.WASTE_MANAGEMENT}</li>
        </ul>
        <h4>{LABELS.WASTE_MANAGEMENT}</h4>
      </div>
      <div className="formGroup" style={{ width: "100%" }}>
        <label>{LABELS.PROJECT_NAME}</label>
        <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
          <div style={{ width: "30%", marginRight: 10 }}>
            <AutoComplete
              id={"projects"}
              list={projects}
              childRef={projectsRef}
              defaultValue={
                selectedRow.id
                  ? {
                      id: selectedRow.projectId,
                      label: selectedRow.projectName,
                    }
                  : null
              }
            />
          </div>
          <Btn text={LABELS.LOAD_GRAPH} onClick={getProjectInfo} />
        </div>
      </div>

      {/* Graphs */}
      {pieChart.series && (
        <div className="graphs">
          <HGraph
            width={"50%"}
            type={"column"}
            xAxis={columnChart.xAxis}
            yAxis={columnChart.yAxis}
            title={" "}
            exportTitle={" "}
          />
          <HGraph
            width={"50%"}
            type={"pie"}
            float={"right"}
            series={pieChart.series}
            title={" "}
            exportTitle={" "}
          />
        </div>
      )}

      {/* Master Grid */}
      {rows ? (
        <ExcelGrid
          id={"wasteManagement"}
          height={window.innerHeight * 0.6}
          cols={cols}
          rows={rows}
          exportOption={true}
          newRowOption={false}
          exportName={LABELS.WASTE_MANAGEMENT}
          onReload={onReload}
          onAdd={onAdd}
        />
      ) : (
        <div className="pageCenter">
          <Loader />
        </div>
      )}

      {/* Form Dialog Box */}
      {manageDialog && (
        <UiDialog
          title={
            selectedRow.id
              ? "Edit " +
                LABELS.WASTE_MANAGEMENT +
                ": " +
                selectedRow.projectName
              : "Add New " + LABELS.WASTE_MANAGEMENT
          }
          open={true}
          size={"xl"}
          onClose={() => {
            setManageDialog(false);
            setFormData({});
            setPageNo(1);
          }}
        >
          <>
            <div style={{ width: "100%", flexDirection: "column" }}>
              <div className="flexRow">
                <div className="formGroup" style={{ width: "45%" }}>
                  <label className="red">{"Subject *"}</label>
                  <TxtBox
                    type={"text"}
                    id="subject"
                    defaultValue={selectedRow.id ? selectedRow.subject : ""}
                  />
                </div>
                <div
                  className="formGroup"
                  style={{ width: "45%", marginLeft: "5%" }}
                >
                  <label className="red">{"Organization *"}</label>
                  <AutoComplete
                    list={orgList}
                    childRef={orgRef}
                    defaultValue={
                      selectedRow.orgName
                        ? {
                            id: selectedRow.orgId,
                            label: selectedRow.orgName,
                          }
                        : null
                    }
                  />
                </div>
              </div>

              <Part1 childRef={part1Ref} editRows={editRows} />

              <PartLast
                childRef={part6Ref}
                selectedRow={selectedRow}
                uploads={uploads}
                setUploads={(val) => {
                  setUploads(val);
                }}
              />

              <ModalBtmBtns
                onSave={manageWasteManagement}
                onCancel={onCloseForm}
                disabled={editRows === null}
              />
            </div>
          </>
        </UiDialog>
      )}
    </React.Fragment>
  );
};

export default withSnackbar(WasteManagement);
